/**
 * useAutomations — TanStack Query wrappers around the
 * ``@ando/core`` automations endpoints.
 *
 * v1 reference: ``ando-frontend/app/components/contexts/AutomationContext.tsx``
 * + ``ando-frontend/app/api/automations.ts``. The v1 ``AutomationContext``
 * was a bespoke React provider doing manual polling, run-state aggregation,
 * and notifications. v2 replaces it with a thin TanStack Query layer so
 * the cache + retry + invalidation pipeline is centralised — every component
 * in the Automations hub reads from the same query keys.
 *
 * Polling cadence:
 *   - List: 30s (matches v1 ``AutomationContext.refreshInterval``).
 *   - Stats: 60s (lower-priority aggregate).
 *   - Notifications: 30s.
 *   - Runs / audit: on-demand only (open the drawer to fetch).
 */

import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  approveRun as approveRunFn,
  createAutomation as createAutomationFn,
  createFromTemplate as createFromTemplateFn,
  deleteAutomation as deleteAutomationFn,
  deleteNotification as deleteNotificationFn,
  duplicateAutomation as duplicateAutomationFn,
  getAuditLog as getAuditLogFn,
  getAutomation as getAutomationFn,
  getAutomationAnalytics as getAutomationAnalyticsFn,
  getAutomationStats as getAutomationStatsFn,
  getUnreadCount as getUnreadCountFn,
  getWebhookUrl as getWebhookUrlFn,
  listAutomationRuns as listAutomationRunsFn,
  listAutomations as listAutomationsFn,
  listNotifications as listNotificationsFn,
  listReactiveSources as listReactiveSourcesFn,
  listTemplates as listTemplatesFn,
  markAllNotificationsRead as markAllNotificationsReadFn,
  markNotificationRead as markNotificationReadFn,
  parseSchedule as parseScheduleFn,
  pauseAutomation as pauseAutomationFn,
  rejectRun as rejectRunFn,
  resumeAutomation as resumeAutomationFn,
  retryRun as retryRunFn,
  runAutomation as runAutomationFn,
  updateAutomation as updateAutomationFn,
  type Automation,
  type AutomationCreatePayload,
  type AutomationUpdatePayload,
} from "@ando/core";

// ── Query keys ─────────────────────────────────────────────────────────

export const automationsQueryKeys = {
  /** Root prefix so ``invalidateQueries(["automations"])`` clears every sub. */
  root: ["automations"] as const,
  list: (page: number, pageSize: number, status?: string) =>
    ["automations", "list", page, pageSize, status ?? null] as const,
  detail: (id: string) => ["automations", "detail", id] as const,
  runs: (id: string, page: number) => ["automations", "runs", id, page] as const,
  audit: (id: string, page: number) =>
    ["automations", "audit", id, page] as const,
  stats: ["automations", "stats"] as const,
  analytics: (days: number) => ["automations", "analytics", days] as const,
  templates: ["automations", "templates"] as const,
  reactiveSources: ["automations", "reactive-sources"] as const,
  webhookUrl: (id: string) => ["automations", "webhook-url", id] as const,
  notifications: (page: number) => ["notifications", "list", page] as const,
  unreadCount: ["notifications", "unread"] as const,
};

// ── Lists / details ────────────────────────────────────────────────────

export function useAutomationsList(args: {
  page?: number;
  pageSize?: number;
  status?: string;
} = {}) {
  const page = args.page ?? 1;
  const pageSize = args.pageSize ?? 20;
  return useQuery(automationsListQueryOptions({ page, pageSize, status: args.status }));
}

/** Shared list query — hook + `/workspace/automations` route loader (intent preload). */
export const automationsListQueryOptions = (args: {
  page: number;
  pageSize: number;
  status?: string;
}) =>
  queryOptions({
    queryKey: automationsQueryKeys.list(args.page, args.pageSize, args.status),
    queryFn: () => listAutomationsFn({ page: args.page, pageSize: args.pageSize, status: args.status }),
    staleTime: 30_000,
    refetchInterval: 30_000,
  });

export function useAutomation(automationId: string | null) {
  return useQuery({
    queryKey: automationsQueryKeys.detail(automationId ?? "__none__"),
    queryFn: () => getAutomationFn(automationId as string),
    enabled: automationId !== null,
    staleTime: 30_000,
  });
}

export function useAutomationRuns(args: {
  automationId: string | null;
  page?: number;
  pageSize?: number;
}) {
  const page = args.page ?? 1;
  return useQuery({
    queryKey: automationsQueryKeys.runs(args.automationId ?? "__none__", page),
    queryFn: () =>
      listAutomationRunsFn({
        automationId: args.automationId as string,
        page,
        pageSize: args.pageSize ?? 20,
      }),
    enabled: args.automationId !== null,
    staleTime: 15_000,
    refetchInterval: (q) => {
      // Speed up polling while there are running/pending runs in view.
      const items = q.state.data?.items ?? [];
      const live = items.some(
        (r) => r.status === "running" || r.status === "pending",
      );
      return live ? 5_000 : 30_000;
    },
  });
}

export function useAutomationAudit(args: {
  automationId: string | null;
  page?: number;
}) {
  const page = args.page ?? 1;
  return useQuery({
    queryKey: automationsQueryKeys.audit(args.automationId ?? "__none__", page),
    queryFn: () =>
      getAuditLogFn({ automationId: args.automationId as string, page }),
    enabled: args.automationId !== null,
    staleTime: 60_000,
  });
}

export function useAutomationStats() {
  return useQuery({
    queryKey: automationsQueryKeys.stats,
    queryFn: getAutomationStatsFn,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}

export function useAutomationAnalytics(days = 30) {
  return useQuery({
    queryKey: automationsQueryKeys.analytics(days),
    queryFn: () => getAutomationAnalyticsFn(days),
    staleTime: 5 * 60_000,
  });
}

export function useAutomationTemplates() {
  return useQuery({
    queryKey: automationsQueryKeys.templates,
    queryFn: listTemplatesFn,
    // Templates are static BE-side — long staleTime, no polling.
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function useReactiveSources() {
  return useQuery({
    queryKey: automationsQueryKeys.reactiveSources,
    queryFn: listReactiveSourcesFn,
    staleTime: 60_000,
  });
}

export function useWebhookUrl(automationId: string | null) {
  return useQuery({
    queryKey: automationsQueryKeys.webhookUrl(automationId ?? "__none__"),
    queryFn: () => getWebhookUrlFn(automationId as string),
    enabled: automationId !== null,
    staleTime: Number.POSITIVE_INFINITY,
  });
}

// ── Notifications ──────────────────────────────────────────────────────

export function useNotificationsList(args: { page?: number } = {}) {
  const page = args.page ?? 1;
  return useQuery({
    queryKey: automationsQueryKeys.notifications(page),
    queryFn: () => listNotificationsFn({ page }),
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
}

export function useUnreadNotificationCount() {
  return useQuery({
    queryKey: automationsQueryKeys.unreadCount,
    queryFn: getUnreadCountFn,
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
}

// ── Mutations ──────────────────────────────────────────────────────────

function useInvalidateAutomations() {
  const qc = useQueryClient();
  return (extraKeys?: readonly unknown[][]) => {
    void qc.invalidateQueries({ queryKey: automationsQueryKeys.root });
    for (const key of extraKeys ?? []) {
      void qc.invalidateQueries({ queryKey: key });
    }
  };
}

export function useCreateAutomationMutation() {
  const invalidate = useInvalidateAutomations();
  return useMutation({
    mutationFn: (payload: AutomationCreatePayload): Promise<Automation> =>
      createAutomationFn(payload),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateAutomationMutation() {
  const invalidate = useInvalidateAutomations();
  return useMutation({
    mutationFn: (args: {
      automationId: string;
      payload: AutomationUpdatePayload;
    }): Promise<Automation> => updateAutomationFn(args.automationId, args.payload),
    onSuccess: () => invalidate(),
  });
}

export function useDeleteAutomationMutation() {
  const invalidate = useInvalidateAutomations();
  return useMutation({
    mutationFn: (automationId: string) => deleteAutomationFn(automationId),
    onSuccess: () => invalidate(),
  });
}

export function useRunAutomationMutation() {
  const invalidate = useInvalidateAutomations();
  return useMutation({
    mutationFn: (automationId: string) => runAutomationFn(automationId),
    onSuccess: () => invalidate(),
  });
}

export function usePauseAutomationMutation() {
  const invalidate = useInvalidateAutomations();
  return useMutation({
    mutationFn: (automationId: string) => pauseAutomationFn(automationId),
    onSuccess: () => invalidate(),
  });
}

export function useResumeAutomationMutation() {
  const invalidate = useInvalidateAutomations();
  return useMutation({
    mutationFn: (automationId: string) => resumeAutomationFn(automationId),
    onSuccess: () => invalidate(),
  });
}

export function useDuplicateAutomationMutation() {
  const invalidate = useInvalidateAutomations();
  return useMutation({
    mutationFn: (automationId: string) => duplicateAutomationFn(automationId),
    onSuccess: () => invalidate(),
  });
}

export function useCreateFromTemplateMutation() {
  const invalidate = useInvalidateAutomations();
  return useMutation({
    mutationFn: (templateId: string) => createFromTemplateFn(templateId),
    onSuccess: () => invalidate(),
  });
}

export function useApproveRunMutation() {
  const invalidate = useInvalidateAutomations();
  return useMutation({
    mutationFn: (args: { automationId: string; runId: string; notes?: string }) =>
      approveRunFn(args),
    onSuccess: () => invalidate(),
  });
}

export function useRejectRunMutation() {
  const invalidate = useInvalidateAutomations();
  return useMutation({
    mutationFn: (args: { automationId: string; runId: string; notes?: string }) =>
      rejectRunFn(args),
    onSuccess: () => invalidate(),
  });
}

export function useRetryRunMutation() {
  const invalidate = useInvalidateAutomations();
  return useMutation({
    mutationFn: (args: { automationId: string; runId: string }) =>
      retryRunFn(args),
    onSuccess: () => invalidate(),
  });
}

export function useMarkNotificationReadMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (notificationId: string) => markNotificationReadFn(notificationId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}

export function useMarkAllNotificationsReadMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => markAllNotificationsReadFn(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}

export function useDeleteNotificationMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (notificationId: string) => deleteNotificationFn(notificationId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}

export function useParseScheduleMutation() {
  return useMutation({
    mutationFn: (args: { text: string; timezone: string }) =>
      parseScheduleFn(args.text, args.timezone),
  });
}
